import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { Button } from '@quebon/ui/components/button'
import { LessonPhotoPanel } from './lesson-photos/LessonPhotoPanel'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { getDateLocale } from '@/utils/dateLocale'
import { useLessonDetail } from '@/hooks/coaching/useLessonDetail'

interface Props {
  learnerId: number
  lessonId: number
  lessonTitle: string
  courseId?: number
  moduleId?: number
  onClose: () => void
}

function fmt(iso: string): string {
  return format(new Date(iso), 'yyyy.MM.dd HH:mm', { locale: getDateLocale() })
}

function fmtMaybe(iso: string | null | undefined): string {
  return iso ? fmt(iso) : '-'
}

/**
 * 레슨 [학습기록] 모달 — 최초 학습 요약 + 복습 회차(designated-review) 회차/시작/완료 일시.
 * 조상에 transform(animate-*) 이 있으면 fixed dim 이 갇히는 함정을 피하려 document.body 로 포털한다.
 */
export function LessonHistoryModal({
  learnerId,
  lessonId,
  lessonTitle,
  courseId,
  moduleId,
  onClose,
}: Props) {
  const { t } = useTranslation('coaching')
  const [photoScope, setPhotoScope] = useState<{ attemptId: number | null; round: number } | null>(null)
  const detail = useLessonDetail(learnerId, lessonId, courseId, moduleId)

  const body = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-bold"><ContentMetadataText text={t('lessonDetail.title', { title: lessonTitle })} /></h2>

        {detail.isLoading ? (
          <p className="text-sm text-gray-500" role="status" aria-live="polite">
            {t('lessonDetail.loading')}
          </p>
        ) : detail.error || !detail.data ? (
          <p className="text-sm text-red-600" role="alert">
            {t('lessonDetail.loadError')}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
              <span>
                {detail.data.lesson.firstCorrectRate != null
                  ? t('lessonDetail.firstCorrectRate', { rate: detail.data.lesson.firstCorrectRate })
                  : t('lessonDetail.correctRateNa')}
              </span>
            </div>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                {t('lessonDetail.historyTitle')}
              </h3>
              <div className="overflow-hidden rounded-md border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs font-medium text-gray-500">
                    <tr>
                      <th className="px-3 py-2">{t('lessonDetail.number')}</th>
                      <th className="px-3 py-2">{t('lessonDetail.startedAt')}</th>
                      <th className="px-3 py-2">{t('lessonDetail.finishedAt')}</th>
                      <th className="px-3 py-2">{t('lessonPhotos.title')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    <tr>
                      <td className="px-3 py-2">1</td>
                      <td className="px-3 py-2">{fmt(detail.data.initialAttempt.startedAt)}</td>
                      <td className="px-3 py-2">
                        {fmtMaybe(detail.data.initialAttempt.completedAt ?? detail.data.lesson.firstCompletedAt)}
                      </td>
                      <td><Button variant="outline" onClick={() => setPhotoScope({ attemptId: null, round: 0 })}>{t('lessonPhotos.view')}</Button></td>
                    </tr>
                    {detail.data.reviews.map((r, idx) => (
                      <tr key={r.sessionNumber} data-testid="review-round">
                        <td className="px-3 py-2">{idx + 2}</td>
                        <td className="px-3 py-2">{fmt(r.startedAt)}</td>
                        <td className="px-3 py-2">{fmtMaybe(r.completedAt)}</td>
                        <td>{r.learningAttemptId != null ? <Button variant="outline" onClick={() => setPhotoScope({ attemptId: r.learningAttemptId ?? null, round: r.sessionNumber })}>{t('lessonPhotos.view')}</Button> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            {photoScope ? <section className="flex flex-col gap-3">
              <h3>{photoScope.attemptId == null ? t('reviewAttempt.progressTab') : t('reviewAttempt.reviewTab', { n: photoScope.round })}</h3>
              <LessonPhotoPanel key={`${learnerId}:${lessonId}:${photoScope.attemptId ?? 'progress'}`} learnerId={learnerId} lessonId={lessonId} attemptId={photoScope.attemptId} readOnly />
            </section> : null}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            {t('lessonDetail.close')}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(body, document.body)
}
